import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Button, DatePicker, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { DATE_RANGE_PRESETS, FILTER_DATE_FORMAT, FILTER_PANEL_VARIANTS } from "../constants"
import { findMatchingPreset } from "../utils"
import styles from "./index.module.css"

const { RangePicker } = DatePicker

/**
 * A date-range filter: a quick-select row over antd's own range calendar.
 *
 * The calendar is antd's, injected through `panelRender` rather than rebuilt, and the picker's
 * text input is hidden — the trigger is whatever button the caller renders, so the panel shows
 * the presets and the calendar and nothing else.
 *
 * `variant` decides only where the calendar sits: `dropdown` lets antd float it against the
 * trigger and is driven by `isOpen`/`onOpenChange`, while `inline` pins it into the surrounding
 * flow of a panel or sheet that is already open.
 */
const DateRangeFilterPanel = ({
  value = null,
  onChange,
  onClose,
  isOpen = true,
  onOpenChange,
  variant = FILTER_PANEL_VARIANTS.DROPDOWN,
  hasFooter = false,
  isCompact = false,
  disabledDate,
  format = FILTER_DATE_FORMAT,
  className = ""
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const hostRef = useRef(null)

  const isInline = variant === FILTER_PANEL_VARIANTS.INLINE

  // The panel stays mounted inside its dropdown, so re-sync whenever the applied range changes
  // from outside — otherwise reopening it would show a stale selection.
  useEffect(() => setDraft(value), [value])

  /** With no footer there is nothing to confirm, so each pick applies as it is made. */
  const commit = (range) => {
    setDraft(range)
    if (!hasFooter) onChange(range)
  }

  /** A preset is a shortcut to a range, so it fills the calendar rather than applying on its own. */
  const activePreset = findMatchingPreset(draft, dayjs().startOf("day"))

  const presetLabel = (preset) =>
    isCompact ? t(preset.shortLabelKey, { defaultValue: preset.shortLabelDefault }) : t(preset.labelKey, { defaultValue: preset.labelDefault })

  const renderPanel = (calendarNode) => (
    <div className={`${styles.panelInner} ${isInline ? "" : styles.paddedInner}`}>
      <div className={styles.section}>
        <span className={styles.sectionLabel}>{t("filters.quick_select", { defaultValue: "Quick Select" })}</span>
        <Flex gap="var(--spacing-xs)" wrap className={styles.presets}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={`${styles.preset} ${activePreset?.key === preset.key ? styles.presetActive : ""}`}
              onClick={() => commit(preset.getRange(dayjs().startOf("day")))}
            >
              {presetLabel(preset)}
            </button>
          ))}
        </Flex>
      </div>

      <span className={styles.sectionLabel}>{t("filters.custom_range", { defaultValue: "Custom Range" })}</span>
      {calendarNode}

      {hasFooter && (
        <Flex gap="var(--spacing-xs)" justify="flex-end" className={styles.footer}>
          <Button
            shape="round"
            onClick={() => {
              setDraft(value)
              onClose?.()
            }}
          >
            {t("filters.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            type="primary"
            shape="round"
            onClick={() => {
              onChange(draft)
              onClose?.()
            }}
          >
            {t("filters.apply", { defaultValue: "Apply" })}
          </Button>
        </Flex>
      )}
    </div>
  )

  return (
    <div ref={hostRef} className={`${styles.host} ${isInline ? styles.inlineHost : ""} ${className}`}>
      <RangePicker
        open={isOpen}
        onOpenChange={onOpenChange}
        value={draft}
        onChange={commit}
        disabledDate={disabledDate}
        format={format}
        panelRender={renderPanel}
        className={styles.hiddenPicker}
        popupClassName={isInline ? styles.inlinePopup : styles.dropdownPopup}
        getPopupContainer={() => hostRef.current || document.body}
      />
    </div>
  )
}

DateRangeFilterPanel.propTypes = {
  /** `[dayjs, dayjs]`, or null for no range. */
  value: PropTypes.array,
  /** Called with the new `[dayjs, dayjs]` or null — on each pick without a footer, on Apply with one. */
  onChange: PropTypes.func.isRequired,
  /** Closes the surface holding the panel; Cancel and Apply call it when there is a footer. */
  onClose: PropTypes.func,
  /** `dropdown` callers drive this from their trigger; an `inline` panel is open by definition. */
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(FILTER_PANEL_VARIANTS)),
  /** Adds Cancel/Apply and defers applying until Apply is pressed. */
  hasFooter: PropTypes.bool,
  /** Shortens the preset labels, for a panel as narrow as a phone sheet. */
  isCompact: PropTypes.bool,
  disabledDate: PropTypes.func,
  format: PropTypes.string,
  className: PropTypes.string
}

export default DateRangeFilterPanel
